import { z } from "zod";
import type { StoryBeatRecord } from "./performances";
import { zImageReference } from "./imageReferenceSchema";
import { zStoryDeliveryLabels } from "./storyDelivery";

const id = z.string().regex(/^[a-zA-Z0-9_-]{1,100}$/);
export const CUSTOMIZATION_WRITING_MODELS = [
  { id: "gpt-5.6-sol", label: "GPT 5.6 Sol" },
  { id: "gemini-4-argon", label: "Gemini 4 Argon" },
  { id: "gemini-3.8-flash", label: "Gemini 3.8 Flash" },
] as const;
/** Private generation preferences, separate from the creative brief and public delivery. */
export const zCustomizationGeneration = z
  .object({
    writingModel: z.enum(["gpt-5.6-sol", "gemini-4-argon", "gemini-3.8-flash"]).optional(),
    artwork: z.enum(["fast", "best", "pro", "openai"]).optional(),
  })
  .strip();
export type CustomizationGeneration = z.infer<typeof zCustomizationGeneration>;
/** Complete ready references retain the original upload, thumbnails, labels and stable picture order. */
export const zStorySourceImage = z
  .object({
    image: zImageReference.refine(
      (image) => !!image.small && !!(image.large || image.original),
      "Prepare the complete source image first."
    ),
    role: z.enum(["subject", "style", "context"]).default("subject"),
    label: z.string().max(200).optional(),
  })
  .strip();
export type StorySourceImage = z.infer<typeof zStorySourceImage>;
/** An approved, reusable direction. Resource targets are story IDs, never labels. */
export const zStoryCustomization = z
  .object({
    generation: zCustomizationGeneration.optional(),
    title: z.string().min(1).max(300).optional(),
    description: z.string().max(16000).optional(),
    labels: zStoryDeliveryLabels.optional(),
    intent: z.string().min(1).max(8000),
    language: z.string().max(100),
    audience: z.string().max(600),
    duration: z.string().max(300),
    durationSeconds: z.number().int().min(10).max(14400).optional(),
    voice: z.boolean(),
    changes: z.array(z.string().min(1).max(1000)).min(1).max(30),
    characters: z
      .array(
        z
          .object({
            roleId: id.optional(),
            name: z.string().min(1).max(100),
            direction: z.string().min(1).max(2000),
            pictureIndexes: z.array(z.number().int().min(1).max(6)).max(6),
          })
          .strip()
      )
      .max(10),
    backdrops: z
      .array(
        z
          .object({
            backdropId: id,
            direction: z.string().min(1).max(2000),
            pictureIndexes: z.array(z.number().int().min(1).max(6)).max(6),
          })
          .strip()
      )
      .max(20),
    additionalScenes: z.array(z.string().min(1).max(2000)).max(5),
  })
  .strip();
export type StoryCustomization = z.infer<typeof zStoryCustomization>;

/** Older saved plans have a human-readable duration only. New plans carry seconds explicitly. */
export function customizationDurationSeconds(plan: StoryCustomization): number | undefined {
  if (plan.durationSeconds) return plan.durationSeconds;
  const match = plan.duration.match(
    /(\d+(?:\.\d+)?)\s*(seconds?|secs?|segundos?|minutes?|mins?|minutos?|hours?|hrs?|horas?)\b/i
  );
  if (!match) return undefined;
  const scale = /^(min)/i.test(match[2]) ? 60 : /^(h)/i.test(match[2]) ? 3600 : 1;
  return Math.round(Number(match[1]) * scale) || undefined;
}
export function customizationDeliveryWords(beats: readonly StoryBeatRecord[]): number {
  return beats.reduce((total, { beat }) => {
    const lines = beat.actions.flatMap((a) =>
      a.kind === "say" || a.kind === "narrate" || a.kind === "text" ? [a.text] : []
    );
    if (beat.interaction?.kind === "respond" && beat.interaction.prompt && !lines.includes(beat.interaction.prompt))
      lines.push(beat.interaction.prompt);
    return total + lines.join(" ").split(/\s+/).filter(Boolean).length;
  }, 0);
}
/** Shared delivery budget: brief transitions plus proportional room for the original core activities. */
export function customizationWordBudget(
  plan: StoryCustomization,
  sceneWords: number,
  storyWords: number,
  sceneCount: number
): number | undefined {
  const seconds = customizationDurationSeconds(plan);
  if (!seconds || !sceneCount) return undefined;
  const total = Math.floor((seconds / 60) * 100);
  const count = sceneCount + plan.additionalScenes.length;
  const base = Math.min(12, Math.floor(total / (count * 2)));
  const average = storyWords / sceneCount;
  return Math.max(
    1,
    Math.floor(
      base +
        ((total - base * count) * (sceneWords || average)) /
          (storyWords + average * plan.additionalScenes.length || count)
    )
  );
}

export function storyCustomizationDirection(plan: StoryCustomization): string {
  return [
    plan.intent,
    `Delivery language: ${plan.language}. Audience and comprehension: ${plan.audience}.`,
    "Preserve interactive beat identities, response roles, saved fork boundaries and interaction/scene continueTo destinations. Translate continueOn answers together with their matching suggestions so direct continuation still works. Adapt spoken questions, invitation context and AI guidance consistently; retain the lasting purpose and current task. Do not replace a saved return scene or recording with generated dialogue. Use only supplied scene/path IDs for any requested routing changes; report unresolved destinations instead of inventing them.",
    `Total story duration: ${plan.duration}. Voice: ${plan.voice ? "enabled" : "disabled"}. This controls audio playback only; keep the character dialogue and narration readable on screen.`,
    ...plan.changes,
    ...plan.characters.map((c) => `${c.name}: ${c.direction}`),
    ...plan.backdrops.map((b) => b.direction),
  ].join("\n");
}
